/** @typedef {Object} alkAuthenticationService
 * @property {Function} logout
 * @property {Function} validate
 * @property {Function} forgotPassword
 * @property {Function} resetPassword
 * @property {Function} changePassword
 * @property {Function} acceptTerms
 */

(function () {
  "use strict";

  angular.module("alk.authentication")
  /** @name alk.authentication.config
   * @ngdoc constant
   * @description Provides configuration settings for the [alkAuthenticationService](alk.authentication.alkAuthenticationService.html) service.
   * @example
   * app.config(["alkAuthenticationServiceProvider", "alk.authentication.config", function (authProvider, authConfig) {
   *   // set up authentication provider
   *   authProvider.dataAnywhereUrl = config.daUrl;
   *   authConfig.applicationUrl = config.mapsureUrl;
   *   authConfig.mapsureUrl = config.mapsureUrl;
   *   authConfig.pcmwsUrl = config.pcmwsUrl;
   *   authConfig.alkmapsApiKey = config.alkmapsApiKey;
   *   authConfig.linkMappingUrl = config.linkMappingUrl;
   * }]);
   * @memberof alk.authentication
   */
    .constant("alk.authentication.config", {
      httpInterceptor: true,

      //  should be the url to the API for the web app
      applicationUrl: "/",

      loginRoute: "login",

      //  url for SSO
      ssoUrl: "",

      //  for validating that a username/password combination is correct.
      validateUrl: "v1/validate",

      //  for resetting a user's password via use of a reset token
      resetPasswordUrl: "v1/password/reset",

      //  for generating a reset password token
      forgotPasswordUrl: "v1/password/forgot",

      //  for changing a user's password
      changePasswordUrl: "v1/password/change",

      //  for accepting the terms of use
      termsOfUseUrl: "v1/user/accept-terms",

      //  for getting user info by auth token
      userInfoUrl: "v1/user",

      //  the name of the current application
      applicationName: "",

      shouldLogoutOnForbidden: function (rejection) {
        if (rejection.config.url.indexOf(this.loginUrl) === 0) {
          return false;
        }

        return true;
      }, 
      skipLogoutOnForbidden: {
        get: function () {
          return config.skipLogoutOnForbidden;
        },
        set: function (value) {
          config.skipLogoutOnForbidden = value;
        }
      },
      extraUserProperties: {}
    })
    .provider("alkAuthenticationService", ["alk.authentication.config",
      /** @name alkAuthenticationService
       * @ngdoc service
       * @param {constant} alk.authentication.config - an [alk.authentication.config](alk.authentication.alk.authentication.config.html) object
       * @description Provides functionality for interfacing with ALK's authentication API.
       * @returns alkAuthenticationService
       * @memberof alk.authentication
       */
      function (config) {
        Object.defineProperties(this, {
          httpInterceptor: {
            get: function () {
              return config.httpInterceptor;
            },
            set: function (value) {
              config.httpInterceptor = value;
            }
          },
          applicationUrl: {
            get: function () {
              return config.applicationUrl;
            },
            set: function (value) {
              config.applicationUrl = value;
            }
          },
          loginRoute: {
            get: function () {
              return config.loginRoute;
            },
            set: function (value) {
              config.loginRoute = value;
            }
          },
          validateUrl: {
            set: function (value) {
              config.validateUrl = value;
            },
            get: function () {
              return config.validateUrl;
            }
          },
          resetPasswordUrl: {
            get: function () {
              return config.resetPasswordUrl;
            },
            set: function (value) {
              config.resetPasswordUrl = value;
            }
          },
          forgotPasswordUrl: {
            get: function () {
              return config.forgotPasswordUrl;
            },
            set: function (value) {
              config.forgotPasswordUrl = value;
            }
          },
          pcmilerUrl: {
            get: function () {
              return config.pcmilerUrl;
            },
            set: function (value) {
              config.pcmilerUrl = value;
            }
          },
          dataAnywhereUrl: {
            get: function () {
              return config.dataAnywhereUrl;
            },
            set: function (value) {
              config.dataAnywhereUrl = value;
            }
          },
          shouldLogoutOnForbidden: {
            get: function () {
              return config.shouldLogoutOnForbidden;
            },
            set: function (value) {
              config.shouldLogoutOnForbidden = value;
            }
          },
          extraUserProperties: {
            get: function () {
              return config.extraUserProperties;
            },
            set: function (value) {
              config.extraUserProperties = value;
            }
          },
          ssoUrl: {
            get: function () {
              return config.ssoUrl;
            },
            set: function (value) {
              config.ssoUrl = value;
            }
          },
          applicationName: {
            get: function () {
              return config.applicationName;
            },
            set: function (value) {
              config.applicationName = value;
            }
          }
        });

        this.$get = ["$http", "$q", "$window", "$location", "UserProvider", function ($http, $q, $window, $location, UserProvider) {

          function _getTrueUrl(url) {
            return config.applicationUrl + url;
          }

          function getUserInfo(authToken, userName, account, forcePasswordReset = false) {
            let basicAuthHeader = userName + ":" + account + ":" + authToken;
            let encodedHeader = "Basic " + window.btoa(basicAuthHeader);

            let userInfoRequest = {
              url: _getTrueUrl(config.userInfoUrl),
              method: "GET",
              headers: {
                Authorization: encodedHeader
              }
            };

            let deferred = $q.defer();
            $http(userInfoRequest)
              .then((resp) => {
                const { data } = resp;
                data.forcePasswordReset = forcePasswordReset
                deferred.resolve(data);
              }, (err) => {
                deferred.reject(err.data);
              });

            return deferred.promise;
          }

          /** validate - logs the user in but does not set any roles
           * Some controller actions need to be accessed while logged in during the signup process (e.g. - getting list of accounts for the user).
           * This authenticates the SSO user so they can use those actions but they currently don"t have an account.
           * @param {ALKLogin} login - An [ALKLogin](alk.authentication.ALKLogin.html) object.
           * @returns {Promise} Returns a [$q](https://docs.angularjs.org/api/ng/service/$q) promise that resolves
           * to the response from the service. If the response from the server is not valid, or if the user is null,
           * it rejects to the response from the service, also.
           * Otherwise, it rejects to the error, if there is one.
           * @memberof alkAuthenticationService
           */
          function validate(login) {
            var deferred = $q.defer();

            $http.post(_getTrueUrl(config.validateUrl), {
              username: login.username,
              password: login.password
            })
              .then(function (raw) {
                let response = raw.data;
                if (response.isValid === true && response.user !== null) {
                  // when validating the user, we are not validating for a specific account or application, not setting account here
                  UserProvider.loadUser(response.user);
                  deferred.resolve(response);
                } else {
                  logout();
                  deferred.reject(response);
                }
              }, function (err) {
                logout();
                deferred.reject(err.data);
              });

            return deferred.promise;
          }

          /** resetPassword - Reset a user's password using a reset token.
           * @param {ALKResetPassword} resetPassword - An [ALKResetPassword](alk.authentication.ALKResetPassword.html) object.
           * @returns {Promise} Returns a [$q](https://docs.angularjs.org/api/ng/service/$q) promise.
           * @memberof alkAuthenticationService
           */
          function resetPassword(resetPassword) {
            return $http.post(_getTrueUrl(config.resetPasswordUrl), {
              email: resetPassword.email,
              newPassword: resetPassword.newPassword,
              token: resetPassword.token
            });
          }

          /** forgotPassword - Send an email with a reset tken to a user that has forgotten their password.
           * @param {ALKForgotPassword} forgotPassword - An [ALKForgotPassword](alk.authentication.ALKForgotPassword.html) object.
           * @returns {Promise} Returns a [$q](https://docs.angularjs.org/api/ng/service/$q) promise.
           * @memberof alkAuthenticationService
           */
          function forgotPassword(forgotPassword) {
            return $http.post(_getTrueUrl(config.forgotPasswordUrl), {
              email: forgotPassword.email
            });
          }

          /** changePassword - Changes a user's password.
           * @param {ALKChangePassword} changePassword - An [ALKChangePassword](alk.authentication.ALKChangePassword.html) object.
           * @returns {Promise} Returns a [$q](https://docs.angularjs.org/api/ng/service/$q) promise.
           * @memberof alkAuthenticationService
           */
          function changePassword(changePassword) {
            return $http.post(_getTrueUrl(config.changePasswordUrl), {
              existingPassword: changePassword.oldPassword,
              newPassword: changePassword.newPassword
            });
          }

          function login(login) {
            var deferred = $q.defer();

            let loginRequest = {
              method: "POST",
              url: `${config.ssoUrl}v2/security/authenticate`,
              data: {
                email: login.username,
                password: login.password,
                account: login.account,
                product: config.applicationName
              },
              withCredentials: true
            };

            $http(loginRequest)
              .then(res => {
                const { data } = res;
                if (data.userId && data.authToken) {
                  if (config.applicationUrl) {
                    const { authToken, username, accountName, forcePasswordReset } = data;
                    getUserInfo(authToken, username, accountName, forcePasswordReset)
                      .then(userInfo => {
                        UserProvider.loadUser(userInfo.user);
                        deferred.resolve(userInfo);
                      }, (err) => {
                        console.log("rejected user info", err);
                        deferred.reject(err);
                      });
                  } else {
                    UserProvider.loadUser(data);
                    deferred.resolve(data);
                  }
                } else {
                  deferred.reject(data);
                }
              }, (err) => {
                deferred.reject(err.data);
              });

            return deferred.promise;
          }

          function verifyService() {
            var deferred = $q.defer();

            let verifyRequest = {
              method: "POST",
              url: `${config.ssoUrl}v2/security/verify`,
              data: {
                product: config.applicationName
              },
              withCredentials: true
            };

            $http(verifyRequest).then((response) => {
              deferred.resolve(response.data);
            }, (err) => {
              deferred.reject(err.data);
            });

            return deferred.promise;
          }

          function verify() {
            var deferred = $q.defer();

            verifyService().then((response) => {
              if (response.userId && response.authToken) {
                if (config.applicationUrl) {
                  getUserInfo(response.authToken, response.username, response.accountName)
                    .then((userInfo) => {
                      UserProvider.loadUser(userInfo.user);
                      deferred.resolve();
                    }, (err) => {
                      console.log("rejected user info", err);
                      deferred.reject(err);
                    });
                } else {
                  UserProvider.loadUser(response);
                  deferred.resolve(response);
                }
              } else {
                deferred.reject(response);
              }
            }, (err) => {
              console.log("verify error", err);
              deferred.reject(err);
            });

            return deferred.promise;
          }

          /** logout - Logout a user by clearing them from the UserProvider, and globally expiring them across other apps.
           * @returns {Promise} Returns a [$q](https://docs.angularjs.org/api/ng/service/$q) promise.
           * @memberof alkAuthenticationService
           */
          function logout() {
            let verifyRequest = {
              method: "POST",
              url: `${config.ssoUrl}v2/security/logout`,
              data: {
                authToken: UserProvider.getUser().authToken
              },
              withCredentials: true
            };


            // clear local user
            UserProvider.clearUser();

            $window.sessionStorage["logoutInProgress"] = true;

            // log the user out of the server
            return $http(verifyRequest);
          }

          /** acceptTerms - accepts the current terms of use
           * @returns {Promise} Returns a [$q](https://docs.angularjs.org/api/ng/service/$q) promise that resolves
           * to nothing or rejects to the error from the service.
           * @memberof alkAuthenticationService
           */
          function acceptTerms() {
            var deferred = $q.defer();

            $http.post(_getTrueUrl(config.termsOfUseUrl), {})
              .then(function (response) {
                deferred.resolve();
              }, function (err) {
                deferred.reject(err);
              });

            return deferred.promise;
          }

          /** getLicensedApps - gets the applications the currently logged in user can access
           * @returns {Promise} Returns a [$q](https://docs.angularjs.org/api/ng/service/$q) promise that resolves
           * to nothing or rejects to the error from the service.
           * @memberof alkAuthenticationService
           */
          function getLicensedApps() {
            var deferred = $q.defer();

            var url = _getTrueUrl("v1/user/licensed-apps");

            $http.get(url).then(raw => {
              const data = raw.data;
              const alkCloudProducts = {
                expired: [],
                unlicensed: [],
                licensed: []
              };
              for (var i = 0; i < data.length; i++) {
                var product = {
                  productId: i,
                  // temporarily remove ALK and CoPilot app name prefixes in front-end
                  // TODO pchen: do this in ALKSSO.TALK_Applications instead
                  productTitle: data[i].name
                    .replace(/^ALK /, "")
                    .replace(/^CoPilot /, ""),
                  productText: data[i].description,
                  productLicensed: data[i].licensed,
                  productAppUrl: data[i].homeUrl,
                  productInfoUrl: data[i].homeUrl,
                  productImgSrc: `/img/${data[i].imageSource}`,
                  productExpired: data[i].expired,
                  showUnlicensedMessage: false
                };

                if (product.productExpired) {
                  alkCloudProducts.expired.push(product);
                } else if (!product.productLicensed) {
                  alkCloudProducts.unlicensed.push(product);
                } else if (product.productLicensed) {
                  alkCloudProducts.licensed.push(product);
                }
              }

              deferred.resolve(alkCloudProducts);
            }, err => {
              console.log(err);
              deferred.reject(err.data);
            });

            return deferred.promise;
          }

          var service = {
            logout: logout,
            validate: validate,
            forgotPassword: forgotPassword,
            resetPassword: resetPassword,
            changePassword: changePassword,
            acceptTerms: acceptTerms,
            getLicensedApps: getLicensedApps,
            getUserInfo: getUserInfo,
            login: login,
            verifyService: verifyService,
            verify: verify
          };

          return service;
        }];
      }
    ])
    .factory("UserProvider", ["$rootScope", "$window", "alk.authentication.config", function ($rootScope, $window, config) {

      function loadUser(user) {
        $rootScope.userInfo = angular.extend({
          isLoggedIn: true
        }, user, config.extraUserProperties);
        $window.sessionStorage["userAuthInfo"] = JSON.stringify($rootScope.userInfo); // jshint ignore:line

        $rootScope.$broadcast("UserLoggedIn", $rootScope.userInfo);
      }

      function init() {
        if ($window.sessionStorage["userAuthInfo"]) { // jshint ignore:line
          loadUser(JSON.parse($window.sessionStorage["userAuthInfo"])); // jshint ignore:line
        }

        // always watch for changes to the user info object and if any are made, than update the session storage correctly.
        $rootScope.$watch("userInfo", function (newValue, oldValue, scope) { // jshint ignore:line
          if (newValue === null) {
            delete $window.sessionStorage["userAuthInfo"]; // jshint ignore:line
            delete $window.sessionStorage["groupSelectedForSession"];
          } else if (angular.equals(newValue, oldValue) === false) {
            $window.sessionStorage["userAuthInfo"] = JSON.stringify(newValue); // jshint ignore:line
          }
        }, true);
      }

      function getUser() {
        if ($rootScope.userInfo) {
          return $rootScope.userInfo;
        }

        var defaultUser = {
          isLoggedIn: false,
          userName: "",
          userId: -1,
          authToken: "",
          account: "",
          roles: []
        };

        // return default - not logged in user
        return angular.extend(defaultUser, config.extraUserProperties);
      }

      function clearUser() {
        // delete the user from session first so if the init function is called, it does not get the stale user from session.
        delete $window.sessionStorage["userAuthInfo"]; // jshint ignore:line
        delete $window.sessionStorage["groupSelectedForSession"];
        $rootScope.userInfo = null;

        $rootScope.$broadcast("UserLoggedOut");
      }

      init();

      return {
        loadUser: loadUser,
        getUser: getUser,
        clearUser: clearUser
      };
    }])
    .factory("alk.authentication.interceptor", ["$window", "$location", "$q", "UserProvider", "Messages", "alk.authentication.config", function ($window, $location, $q, UserProvider, Messages, alkConfig) {
      return {
        request: function (config) {

          if (alkConfig.httpInterceptor === false) {
            return config;
          }

          var user = UserProvider.getUser();
          if (!user.isLoggedIn && !window.localStorage.getItem("jwtToken")) {
            return config;
          }

          if (!config.headers) {
            config.headers = {};
          }

          if (config.url.indexOf(alkConfig.dataAnywhereUrl) === 0) {
            // we need to check to see if we are currently in a support account.
            // if we are in a support account, we have an encrypted owner id.
            // we do not want to send encrypted owner IDs as our authentication
            // mechanism for the owner ID route. send our auth token.
            if (user.supportOwnerId && config.url.indexOf("/api/owner/") === -1) {
              // attach authorization with the encrypted owner id
              config.headers.Authorization = "pcmws=" + user.supportOwnerId;
            } else if (window.localStorage.getItem("jwtToken") && $location.url().startsWith("/place-editor")) {
              // We must use token when on place-editor
              config.headers.Authorization = `bearer ${window.localStorage.getItem("jwtToken")}`;
            } else {
              // attach authorization header to all requests made to data anywhere
              config.headers.Authorization = "token=" + user.authToken;
            }
          } else if (config.url.indexOf("/") === 0 || config.url.indexOf(alkConfig.applicationUrl) === 0) {
            // attach authorization header to all requests made to the this website.
            var basicAuthHeader = user.userName + ":" + user.account + ":" + user.authToken;

            if (user.supportOwnerId)
              basicAuthHeader += ":" + user.supportOwnerId;

            if (config.headers.Authorization === undefined)
              config.headers.Authorization = "Basic " + window.btoa(basicAuthHeader);
          } else if (config.url.indexOf(alkConfig.pcmwsUrl) === 0) {
            // if Web UI, use region-specific reportApiKey; if Mapsure, use WW alkmapsApiKey
            let apiKey;
            if (alkConfig.reportApiKey) {
              apiKey = alkConfig.reportApiKey[UserProvider.getUser().region || "NA"];
            } else {
              apiKey = alkConfig.alkmapsApiKey;
            }
            // attach authorization header in the style of FleetPortal: API Key | authToken | date
            config.headers.Authorization = apiKey + "|" + user.authToken + "|" + new Date().toISOString();

            if (user.supportOwnerId) {
              config.headers.Authorization += "|" + user.supportOwnerId;
            }
          }

          return config;
        },
        responseError: function (rejection) {
          if (alkConfig.httpInterceptor === false || alkConfig.shouldLogoutOnForbidden(rejection) === false || alkConfig.skipLogoutOnForbidden === true) {
            return $q.reject(rejection);
          }

          var clearUser = false;

          // The expected use of this is to capture the case of when a user was authenticatd but it has expired, to send them to the login screen to re-login.
          // If the "unauthorized" error is coming from trying to login originally, don"t redirect them
          if (rejection.status === 401 && rejection.config.url.indexOf("/") === 0) {
            // if the request is unauthorized (status code 401), log the user out and bring them to the login screen.  This would happen if the token ever expires after the user is logged in.
            Messages.add("danger", "Please login again.", {
              showOnNextPage: true
            });
            clearUser = true;
          } else if ((rejection.status === 0 || rejection.status === 403) && rejection.config.url.indexOf(alkConfig.dataAnywhereUrl) === 0) {
            // if data anywhere sends a status of 0, it means that the token passed is invalid, log the user out and make them log in again.
            Messages.add("danger", "Please login again.", {
              showOnNextPage: true
            });
            clearUser = true;
          } else if (rejection.status === 403 && rejection.config.url.indexOf(alkConfig.pcmilerUrl) === 0) {
            Messages.add("danger", "Please login again.", {
              showOnNextPage: true
            });
            clearUser = true;
          }

          if (clearUser === true) {
            UserProvider.clearUser();
            $location.path(alkConfig.loginRoute);
          }

          return $q.reject(rejection);
        }
      };
    }])
    .config(["$httpProvider", function ($httpProvider) {
      $httpProvider.interceptors.push("alk.authentication.interceptor");
    }]);

})();




// WEBPACK FOOTER //
// ./Common Javascript Files/AngularJS/alk-authentication/service/alkAuthenticationService.js